import { Injectable } from '@angular/core';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { ObservableWorkStateMap } from '@vendasta/rx-utils/work-state';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

export interface AccountNameData {
  accountGroupId: string;
  name: string;
  companyId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccountNameGetterService {
  private searchedAccounts = new Map<string, boolean>();

  private accounts = new ObservableWorkStateMap<string, AccountNameData>();

  private readonly loading$$ = new BehaviorSubject(false);
  readonly loading$ = this.loading$$.asObservable().pipe(shareReplay(1));

  constructor(private readonly accountGroupService: AccountGroupService) {}

  loadAccountIfNotLoaded(accountGroupId: string): void {
    if (this.searchedAccounts.has(accountGroupId) === false || this.searchedAccounts.get(accountGroupId) === false) {
      this.accounts.startWork(accountGroupId, this.get(accountGroupId));
    }
  }

  getAccount$(accountGroupId: string): Observable<AccountNameData> {
    return this.accounts.getWorkResults$(accountGroupId).pipe(shareReplay(1));
  }

  accountLoading$(accountGroupId: string): Observable<boolean> {
    return this.accounts.isLoading$(accountGroupId);
  }

  isAccountDeleted$(accountGroupId: string): Observable<boolean> {
    return this.accounts.getWorkResults$(accountGroupId).pipe(
      map((account) => {
        return !account.name;
      }),
      shareReplay(1),
    );
  }

  private get(accountGroupId: string): Observable<AccountNameData> {
    const projectionFilter = new ProjectionFilter({ napData: true, accountGroupExternalIdentifiers: true });
    return this.accountGroupService.get(accountGroupId, projectionFilter).pipe(
      map((a) => {
        this.searchedAccounts.set(accountGroupId, true);
        return <AccountNameData>{
          accountGroupId: accountGroupId,
          name: a?.napData?.companyName || '',
          companyId: a?.externalIdentifiers?.platformCrmCompanyId,
        };
      }),
    );
  }
}
